import Handlebars from 'handlebars';
import uuidv4 from 'uuid/v4'; 

import i18n from '../i18n';
import Collection from '../collection';
import Config from '../config'; 
import Model from '../model/model';

import { idgen } from '../utils/idgen';
import { hashify } from '../utils/arrays';
import { fmt, datify, unixts, is, today, yesterday, prevMonth, nextMonth, beginDay, tomorrow } from '../utils/date';
import { cond } from './handlebars.cond';

let ifn; i18n.then( i => ifn = i );

const caseStack = [];

const guidStack = [];

const constants = {
    NOT: '@@@NOT@@@',
    OR: '@@@OR@@@',
    AND: '@@@AND@@@',
    EQ: '@@@EQ@@@',
    EQQ: '@@@EQQ@@@',
    LESS: '@@@LESS@@@',
    MORE: '@@@MORE@@@',
    T: '@@@T@@@',
    F: '@@@F@@@',
    BEGIN: '@@@BEGIN@@@',
    END: '@@@END@@@',
    
    BODY_INJECTOR: '@@@BODY_INJECTOR@@@',
    PARENT_VIEW: '@@@PARENT_VIEW@@@',
    INJECTED_NAME: '@@@INJECTED_NAME@@@',
    PASSED_PARTIAL_BLOCK_HELPER: '@@@PASSED_PARTIAL_BLOCK_HELPER@@@',
};

let zoneHash;

const helpers =  {

    cond,

    binary(predicate, value) {
        return predicate ? value : undefined;
    },

    ternary(predicate, value, elseValue) {
        return predicate ? value : elseValue;
    },

    print(value) {
        return value !== undefined ? value : '';
    },

    concat(...values) {
        return values.pop(), values.join( '' );
    },

    extend(array, ...values) {
        return values.pop(), [...array, ...values]
    },

    pick(...values) {
        return values.pop(), values
            .filter( value => value != undefined )
            [0];
    },

    either(...args) {
        return args.pop(), args.filter( arg => arg !== undefined )[0];
    },

    escape(value) {
        return value != undefined
            ? value.replaceAll( '\\', '\\\\' ).replaceAll( '"', '&quot;' )
            : value;
    },

    contains(bigger, smaller) {
        if (bigger == null)
            bigger = [];
        if (bigger instanceof Collection)
            bigger = bigger.toJSON();

        if (bigger instanceof Array)
            if (bigger[0])
                if (bigger[0].id)
                    bigger = bigger.map( b => b.id );

        if (typeof smaller === 'string')
            return bigger.includes( smaller ) || bigger.includes( +smaller );
        else if (typeof smaller === 'number')
            return bigger.includes( smaller ) || bigger.includes( '' + smaller );

        if (bigger instanceof Array)
            bigger = hashify( bigger );
        if (!(smaller instanceof Array))
            smaller = [smaller];

        return smaller.every( item => bigger[item] );
    },

    groups(size, collection) {
        if (collection instanceof Collection)
            collection = collection.toJSON();

        const result = [];
        for (let now = [], i = 0; i < collection.length; i++) {
            now.push( collection[i] );
            if (now.length == size || i == collection.length - 1)
                result.push( now ),
                now = [];
        }

        return result;
    },

    sort(array) {
        return [...array].sort();
    },

    pluck(attr, collection) {
        if (collection instanceof Collection)
            collection = collection.toJSON();

        return collection.map( model => model[attr] );
    },

    json(model) {
        return model.toJSON() ? model.toJSON() : model;
    },

    odd(value) {
        return value % 2;
    },

    or(...values) {
        // pop - to drop "options"
        return values.pop(), values.some( val => !!val );        
    },

    and(...values) {
        // pop - to drop "options"
        return values.pop(), values.every( val => !!val );        
    },

    not(value) {
        return !value;
    },

    dehtmlify(value) {
        return value && flatify.call( this, value ).replace( /<p>/, '' ).replace( /<p>/g, ' ' );
    },

    flatify(value) {
        return flatify.call( this, value );
    },

    json(value) {
        return value instanceof Model
            ? value.toJSON()
            : value instanceof Collection ? value.toJSON() : value;
    },

    global(refbookItems) {
        return refbookItems instanceof Collection   
            ? refbookItems.toJSON().filter( item => !item.cluster )
            : refbookItems.filter( item => !item.cluster );
    },

    nonglobal(refbookItems) {
        return refbookItems instanceof Collection   
            ? refbookItems.toJSON().filter( item => !!item.cluster )
            : refbookItems.filter( item => !!item.cluster );
    },

    cut(length, value) {
        let [string] = value.trim().split( ' ' )
            .reduce( ([str, end], word) => end ? [str, end]
                : !str.length
                    ? [word, false]
                    : str.length + word.length + 1 <= length
                        ? [`${str} ${word}`, false]
                        : [str, true], ['', false] );

        if (string.length === value.trim().length)
            return string;

        while (string.length)
            if (string.match( /[а-яА-Яa-zA-Z0-9]$/ ))
                break;
            else
                string = string.substr( 0, string.length - 1 );

        return string ? `${string}...` : '';
    },

    'hold'(...args) {
        const uuid = uuidv4();
        const options = args.pop();
        const parent = options.data[constants.PARENT_VIEW];

        if (parent)
            return parent.holds[uuid] = args, uuid;
    },

    '#iff'(...args) {
        const options = args.pop();
    
        return tru( evaluate( args ) )
            ? options.fn( this )
            : options.inverse( this );
    },
    
    '#View': makeViewHelper( './view' ),
    
    '#InjectBody'(options) {
        if (options.data[constants.BODY_INJECTOR])
            return new Handlebars.SafeString( options.data[constants.BODY_INJECTOR]( this, options ) );
    
        return '';
    },
    
    '#case'(context, options) {
        caseStack.push( context );
        const val = options.fn( this );
        caseStack.pop();
        return val;
    },

    '#of'(context, options) {
        if (context === caseStack[caseStack.length - 1])
            return options.fn( this );
    },

    '#ifOneOf'(context, ...values) {
        let options = values.pop();
        return ~values.indexOf( context )
            ? options.fn( this )
            : options.inverse( this );
    },
    
    ':dt'(date, format) {
        return date ? fmt( datify( date ), format ) : '';
    },

    'dt'(date, format) {
        return date ? fmt( datify( date ), format ) : '';
    },

    dtlower(date) {
        return beginDay( datify( date ) );
    },

    dtupper(date) {
        return tomorrow( beginDay( datify( date ) ) );
    },

    datify(value) {
        return datify( value );
    },

    now() {
        return new Date();
    },

    'prev-month'(date) {
        return prevMonth( date ); 
    },

    'next-month'(date) {
        return nextMonth( date ); 
    },

    'unixts'(date) {
        return date ? unixts( datify( date ) ) : undefined;
    },

    'past'(date) {
        return new Date() > datify( date );
    },

    '#row'({ hash, fn }) {
        const { style, toggle } = hash;
        return `<div class="r row ${hash['show-grid' ? 'show-grid' : '']}" style="${style || ''}" ${toggle ? `data-bind="toggle:${toggle}"`: ''}>${fn( this )}</div>`;
    },

    '#col': makeColumn( undefined ),

    '#col1': makeColumn( 1 ),

    '#col2': makeColumn( 2 ),

    '#col3': makeColumn( 3 ),

    '#col4': makeColumn( 4 ),

    '#col5': makeColumn( 5 ),

    '#col6': makeColumn( 6 ),

    '#col7': makeColumn( 7 ),

    '#col8': makeColumn( 8 ),

    '#col9': makeColumn( 9 ),

    '#col10': makeColumn( 10 ),

    '#col11': makeColumn( 11 ),

    '#col12': makeColumn( 12 ),

    'tabs'({ fn, hash: { class: klass, style } }) {
        return `<ul class="nav nav-tabs ${klass}" style="margin-bottom: 20px; ${style}">${fn( this )}</ul>`;
    },

    'pills'({ fn, hash: { class: klass, style, ['color-scheme']: colorScheme, toggle } }) {
        return `<nav class="nav nav-pills ${klass} ${colorScheme == 'purple' && '--color-scheme-purple'}" style="${style}" ${!toggle ? '' : `data-bind="toggle:${toggle}"`}>${fn( this )}</nav>`;
    },

    'tab': makeNavItemHelper( '' ),

    'pill': makeNavItemHelper( 'padding-top: 7px; padding-bottom: 8px; ' ),

    'buttons'({ hash, fn }) {
        const {
            class: className,
            style
        } = hash;

        return `<div 
            class="btn-group ${className || ''}" 
            ${style ? `style="${style}"` : ''} 
            role=group aria-label=...>${fn( this )}</div>`;
    },

    'button'({ hash, fn }) {
        const { label } = hash;
        const disabledValue = hash['disabled'];
        const enabledValue = hash['enabled'];

        let disabled = false;

        if (~['t', 'f', 'true', 'false', true, false].indexOf( disabledValue ))
            disabled = bool( disabledValue );
        else if (disabledValue !== undefined)
            disabled = !!disabledValue;
        else if (~['t', 'f', 'true', 'false', true, false].indexOf( enabledValue ))
            disabled = !bool( enabledValue );
        else if (enabledValue !== undefined)
            disabled = !enabledValue;

        const body = `<button type=button 
            class="btn btn-default -${hash['action']} ${hash['active'] ? 'active' : ''}"
            ${hash['name'] ? `name="${hash['name']}"` : ``}
            ${hash['data-click-trigger'] ? `data-click-trigger="${hash['data-click-trigger']}"` : ''}
            ${disabled ? 'disabled' : ''}
            >${label ? ifn( label ) : fn( this )}</button>`;

        return label
            ? new Handlebars.SafeString( body )
            : body;
    },

    'form': makeViewHelper( './form' ),

    'grid-context'({ fn, data }) {
        const [activeIndex] = this.items
            .map( (it, index) => [it, index] )
            .filter( ([it]) => it.active )
            .map( ([it, index]) => index );

        data = Handlebars.createFrame( data );
        data.activeIndex = activeIndex;
        data.columns = [];

        return fn( this, { data } );
    },

    'gh'({ fn, hash: { title, label, width, style }, data }) {
        data.columns.push( undefined );

        const html = `<th title="${title ? title : ''}" style="${width ? 'width:' + width : ''};${style ? style : ''}">
            <span class="grid-head">
                ${label ? ifn( label ) : fn ? fn( this ) : ''}
            </span></th>`;

        return new Handlebars.SafeString( html );
    },

    'gd'({ fn, hash: { title, text, width, style, colspan, rowspan, ...hash } }) {
        const html = `<td title="${title ? title : ''}" 
            style="${width ? `width:${width}` : ''};${style ? style : ''}"
            ${colspan ? `colspan="${colspan}"`: ''}
            ${rowspan ? `rowspan="${rowspan}"`: ''}
            >
            <span class="grid-cell" ${hash['line-height'] ? `style="line-height:${hash['line-height']}"` : ''}>
                ${text ? ifn( text ) : fn ? fn( this ) : ''}
            </span></th>`;

        return new Handlebars.SafeString( html );
    },

    'gr-split'({ data: { index, activeIndex } }) {
        return new Handlebars.SafeString( `</tr><tr data-index="${index}" 
            class="secondary-row ${index === activeIndex ? 'info' : ''}">` );
    },

    'grid-column-counter'({ data }) {
        return data.columns.length;
    },

    'timeleft'(date) { 
        return interval( unixts( date ) - unixts( new Date() ) );
    },

    'timepassed'(date) { 
        if (is( date, today() ))
            return interval( unixts( new Date() ) - unixts( date ) );
        else if (is( date, yesterday( today() )))
            return `${ifn( 'dates::yesterday' )}, ${fmt( date, 'HH:mm' )}`;
        else if (fmt( date, 'MM.YYYY' ) === fmt( today(), 'MM.YYYY' ))
            return fmt( date, 'DofMonHHmm' );
        else if (fmt( date, 'YYYY' ) === fmt( today(), 'YYYY' ))
            return fmt( date, 'DofMon' );
        else
            return fmt( date, 'DofMonYYYY' );
    },

    ':interval'(seconds) {
        return interval( seconds );
    },

    'interval'(seconds) {
        return interval( seconds );
    },

    'tokenize'(url) {
        return Config.url( `/${url}` );
    },
    
    '#fileurl'(options) {
        return Config.url( `/department_files/${options.fn( this )}` ).replace( '/rest', '' );
    },
    
    ':html-drop-dataset'() {
        const html = Object.keys( this )
            .filter( key => 0 === key.indexOf( 'data-' ) )
            .map( key => `${key}="${this[key]}"` )
            .join( ' ' );
    
        return new Handlebars.SafeString( html );
    },

    __bodycall__(options) {
        return new Handlebars.SafeString( options.data.options[constants.PASSED_PARTIAL_BLOCK_HELPER]( this, options ) );
    },

    'log'(...values) {
        return values.pop(), console.log.apply( console, values ), '';
    },

    ':log'(...values) {
        return values.pop(), console.log.apply( console, values ), '';
    },

    'tick'(value) {
        return 't' === value || true === value 
            ? new Handlebars.SafeString( '&#10003;' ) 
            : '';
    },

    '#genguid'(options) {
        guidStack.push( uuidv4() );
        const html = options.fn( this );
        return guidStack.pop(), html;
    },

    'guid'() {
        return guidStack[guidStack.length - 1] || uuidv4();
    },

    'dict'({ hash }) {
        const context = {};
        for (let propName in hash)
            context[propName] = dictFn( hash[propName] );

        return context;
    },

    'names'(values, { hash: { collection, dict, nothingSelectedLabel, nItemsSelectedLabel, nItemsSelectedLimit } }) {
        if (!dict)
            dict = dictFn( collection );

        if (!(values instanceof Array))
            values = values === null || values === undefined
                ? []
                : [values];

        const filtered = values.map( item => ifn( dict
            [item instanceof Object ? item.id : item] ) );
        if (filtered.length === 0)
            return ifn( nothingSelectedLabel || '' );
        else if (nItemsSelectedLabel && filtered.length >= (nItemsSelectedLimit || 3))
            return ifn( nItemsSelectedLabel, { count: filtered.length } );
        else
            return filtered.join( ', ' ) || '';
    },

    'custom-field-prop'(translit, path, options) {
        if (!options)
            [options, path] = [path, undefined];

        const { data: { model } } = options;

        const fields = (path || '')
            .split( '|' )
            .map( part => part.split( '=' ) )
            .map( ([id, encodedTranslit, index]) => 
                [decodeURIComponent( encodedTranslit ).replaceAll( '[dot]', '.' ).replaceAll( '[apo]', "'" ), index] )
            .reduce( (fields, [translit, index]) => 
                fields && fields[translit] && fields[translit].value && fields[translit].value[index] || undefined, 
                model.get( 'fields' ) )
            || model.get( 'fields' );

        return fields && fields[translit] && fields[translit].value || undefined;
    },

    // always 2 params + "options"
    'namify'(translit, path/*, options */) {
        if (!path)
            return translit;

        const breadcrumbs = path.split( '|' )
            .map( part => part.split( '=' ) )
            .map( ([id, translit, index]) => `${translit}[${index}]` );

        return `${breadcrumbs.join( '.' )}.${encodeURIComponent( translit.replaceAll( '.', '[dot]' ).replaceAll( "'", '[apo]' ) )}`;
    },

    'prop'(dottedProps, ...args) {
        const { data: { model } } = args.pop();

        const fn = (value, [head, ...tail]) =>
            !head || !value
                ? value
                : head.startsWith( '$' )
                    ? fn( value[args[head.substring( 1 )]], tail )
                    : fn( value[head], tail );

        const [head, ...tail] = dottedProps.split( '.' );
        return fn( model.get( head ), tail );
    },

    'length'(array) {
        if (array instanceof Object)
            return 'length' in array ? array.length : undefined;
        return undefined;
    },

    'diff'({ data }) {
        if (!data.model)
            return undefined;
        
        let view = data[constants.PARENT_VIEW];
        while (view)
            if (view.diff)
                return view.diff();
            else if (view.options[constants.PARENT_VIEW])
                view = view.options[constants.PARENT_VIEW];
            else
                return undefined;
    },

    'collection'(collection, options) {
        if (collection instanceof Collection)
            collection = collection.toJSON();

        if (!(collection instanceof Array))
            if (typeof collection === 'number') { 
                let left = collection; 
                collection = [];
                while (left--)
                    collection.unshift( left );
            }
            else
                return;

        const data = options.data &&
            Handlebars.createFrame( options.data );

        const contentItems = [];

        for (let index = 0; index < collection.length; index++) {
            if (data)
                data.index = index;

            contentItems.push( options.fn( collection[index], { data } ) );
        }

        return contentItems.join( '' );
    },

    'takeFirst'(items) {
        for (let item of items)
            if (item) return item;
    },

    'join'(delimiter, items, { fn }) {
        return fn
            ? items.map( fn ).join( delimiter )
            : items.join( delimiter );
    },

    'gt'(a, b) {
        return (a instanceof Date ? unixts( a ) : a) 
            > (b instanceof Date ? unixts( b ) : b);
    },

    'lt'(a, b) {
        return (a instanceof Date ? unixts( a ) : a) 
            < (b instanceof Date ? unixts( b ) : b);
    },

    'base64'(string) {
        return Base64.encodeURI( string ); 
    },

    'encodeTranslit'(fieldTranslit) {
        return encodeURIComponent( fieldTranslit.replaceAll( '.', '[dot]' ).replaceAll( "'", '[apo]' ) );
    },

    'subtract'(a, b) {
        return a - b;
    },

    'sum'(...items) {
        return items.pop(), items.reduce( (total, el) => total + +el, 0 );
    },

    'inc'(x) {
        return x + 1;
    },

    'dec'(x) {
        return x - 1;
    },

    'min'(...items) {
        items.pop();
        let result = undefined;
        for (let item of items) if (item != undefined)
            if (result === undefined || +item < result)
                result = +item;
                
        return result;
    },

    'max'(...items) {
        items.pop();
        let result = undefined;
        for (let item of items) if (item != undefined)
            if (result === undefined || +item > result)
                result = +item;

        return result;
    },

    'int'(value) {
        return +value;
    },

    'take'(count, items) {
        return !!items ? items.filter( (_, index) => index < count ) : items;
    },

    'tz'(id) {
        if (!zoneHash)
            zoneHash = hashify( Config.timezones(), zone => [zone.id, zone.name] );

        if (!zoneHash)
            return id;

        return zoneHash[id];
    },

    'sortedi'(items, prop) {
        if (!prop)
            return [...items].sort();

        return [...items]
            .sort( (a, b) => a[prop] - b[prop] );
    },

};

export { constants, helpers, interval };

function flatify(value) {
    if (!value) return value;
    return value
        .replace( /<(\/?(strong|b|em|span|ul|ol)|\/li)>/g, '' )
        .replace( /<li>/g, '[paragraph]' )
        .replace( /<[^>]+>/g, ' ' )
        .replace( /\[paragraph\]/g, '<p>' )
        .replace( /\s\s+/g, ' ' )
        .trim();
}

function dictFn(collection, ...propNames) {
    if (!propNames.length)
        propNames = ['name', 'title'];

    if (typeof collection === 'function')
        collection = collection();
    else if (collection instanceof Collection)
        collection = collection.toJSON();

    const takeFirst = (item, props) =>
        props.map( name => item[name] )
            .filter( value => value !== undefined )
            [0];

    return hashify( collection, 
        item => [item.id, takeFirst( item, propNames )] );
}

function makeColumn(width) {
    return function(w, options) {
        if (width) options = w, w = undefined;

        const { offset, style } = options.hash;

        const styleString = style
            ? ` style="${style}"`
            : '';

        const classString = offset
            ? ` class="col col-lg-${width || w} col-lg-offset-${offset}"`
            : ` class="col col-lg-${width || w}"`;

        return `<div ${classString}${styleString}>${options.fn( this )}</div>`;
    };
}

function makeNavItemHelper(style) {
    return function({ fn, hash }) {
        let { 
            toggle,
            disabled, enabled, active,
            label, icon, href, action, 
            class: className, style: altStyle,
            dense, darker, // for pill
            clickableActive,
            ...otherHashAttrs 
        } = hash;

        if (disabled !== undefined)
            disabled = bool( disabled );
        else if (enabled !== undefined)
            disabled = !bool( enabled );
        else
            disabled = false;

        active = active !== undefined && bool( active );

        const attrsString = [];
        for (let attrName in otherHashAttrs)
            if (attrName !== 'bind-active')
                attrsString.push( `${attrName}="${otherHashAttrs[attrName]}"` );

        const text = label ? ifn( label ) : fn ? fn( this ) : '';

        const body = `<li role=presentation ${disabled || active && !clickableActive ? 'disabled' : ''} 
            ${!toggle ? '' : `data-bind="toggle:${toggle}"`}
            class="${active ? 'active' : ''} ${darker ? '__pill-darker' : ''} ${className ? className : ''}"
            ${otherHashAttrs['bind-active'] ? `data-bind="${otherHashAttrs['bind-active']}"` : ''}>
            <a  href="${href && (!active || clickableActive) ? href : 'javascript:void(0);'}" 
                style="${style} ${dense ? 'padding-top:3px;padding-bottom:3px;' : ''} ${altStyle ? altStyle : ''}"
                ${attrsString.join( ' ' )}
                class=-${action}>${icon ? `<span class="glyphicon glyphicon-${icon}"></span>` : ''}${icon && text.trim() ? '&nbsp;' : ''}${text}</a></li>`;

        return new Handlebars.SafeString( body );
    };
}

function makeViewHelper(defaultSrc) {
    return function(options) {
        const {
            src: viewModuleSrc = defaultSrc, 
            inject: viewInjectedName, 
            model: passedModel,
            class: className,
            style,
            ...passedOptions
        } = options.hash;

        const {meta} = options.data,
            {caps: capabilities} = options.data;

        const htmlNestId = idgen(),
            htmlNestPlaceholder = `<script type="x-placeholder" id="${htmlNestId}"></script>`;

        // throw out "./" from the beginning of "src"
        // and resolve to "view" dir
        const importing = System.import( `../view/${viewModuleSrc.substr( 2 )}` );
        meta.views[htmlNestId] = importing.then( module => {
            const parentView = options.data[constants.PARENT_VIEW];

            // view must be injected into parent
            // if parent exists and "inject" parameter is passed
            const mustBeInjected = parentView && viewInjectedName !== undefined;

            // copy {{#View}} options to this view's options
            const viewOptions = { 
                ...passedOptions,
                className, style,
                injectedName: viewInjectedName,
                [constants.BODY_INJECTOR]: options.fn,
                [constants.PARENT_VIEW]: parentView,
                [constants.INJECTED_NAME]: mustBeInjected && viewInjectedName,
                // pass partial's block to #View as it's options
                [constants.PASSED_PARTIAL_BLOCK_HELPER]: options.data['partial-block']
            };

            // check: maybe view already has been rendered
            // and contained in its parent view?
            let view;
            if (mustBeInjected)    
                view = parentView[viewInjectedName];

            if (view && !(view instanceof module.default))
                view = new (module.default)( viewOptions );
            if (!view)
                view = new (module.default)( viewOptions );

            if (mustBeInjected)
                parentView[viewInjectedName] = view;

            // assign options (if not first-time render)
            Object.assign( view.options, viewOptions );
                
            // assign view's el its new name in view hierarchy
            view.$el.attr( 'name', viewInjectedName );
                
            // run rendering; create rendering context before
            // by just passing current rendering context
            // todo: I don't know why copy capabilities directly here
            const newContext = Object.create( this );
            if (passedModel && passedModel.capabilities_)
                newContext.capabilities = { ...passedModel.capabilities_ };
            else if (capabilities)
                newContext.capabilities = { ...capabilities };

            const modelToBind = passedModel !== undefined
                ? passedModel
                : parentView
                    // view must be bound to parent's model if parent view exists 
                    // todo: does it even work?
                    ? parentView.model
                    : view.model;

            // note: pass "renderer" function to bind() to catch desired context and function-finalizer
            // if no modelToBind found, calling bind(undefined)
            // is equivalent of calling unbind()
            const rendering = view.bind( modelToBind, 
                () => view.render( newContext, meta.finalizeSubviews ) )

            return { 
                id: htmlNestId, 
                view, 
                rendering,
                prepared: rendering.prepared,
            };
        } );

        // return view placeholder which will be inserted into html
        // and replaced by actual view after its rendering
        return htmlNestPlaceholder;
    };
}

function evaluate(args) {
    return args.reduceRight( (stack, op) => {
        switch (op) {
            case constants.NOT: return not( stack );
            case constants.AND: return and( stack );
            case constants.OR: return or( stack );
            case constants.EQ: return eq( stack );
            case constants.EQQ: return eqq( stack );
            case constants.LESS: return less( stack );
            case constants.MORE: return more( stack );
            case constants.T: return t( stack );
            case constants.F: return f( stack );
            case constants.BEGIN: return begin( stack );
            case constants.END: return end( stack );
            default: return cnst( op, stack );
        }
    }, [] );
}

function not([head, ...tail]) {
    return [!tru( head ), ...tail];
}

function and([head, ...tail]) {
    if (head !== constants.BEGIN)
        return [[head, ...tail].every( tru )];

    // throw out left @{ by not including head
    const args = [];
    // search for right @}
    while (tail[0] !== constants.END) 
        args.push( tail.shift() );
    // throw out right @}
    tail.shift();
    // build result
    return [args.every( tru ), ...tail]
}

function or([head, ...tail]) {
    if (head !== constants.BEGIN)
        return [[head, ...tail].some( tru )];

    // throw out left @{ by not including head
    const args = [];
    // search for right @}
    while (tail[0] !== constants.END) 
        args.push( tail.shift() );
    // throw out right @}
    tail.shift();
    // build result
    return [args.some( tru ), ...tail]
}

function eq([x, y, ...tail]) {
    if (x instanceof Date && y instanceof Date)
        // 1-minute diff
        return [Math.abs(x - y) < 60 * 1000, ...tail];
    else 
        return [x == y, ...tail];
}

function eqq([x, y, ...tail]) {
    return [x === y, ...tail];
}

function less([x, y, ...tail]) {
    return [x < y, ...tail];
}

function more([x, y, ...tail]) {
    return [x > y, ...tail];
}

function t([head, ...tail]) {
    return [head === true || head === 'true' || head === 't',
        ...tail];
}

function bool(value) {
    return t( [value] )[0];
}

function f([head, ...tail]) {
    return [head === false || head === 'false' || head === 'f',
        ...tail];
}

function begin(stack) {
    return [constants.BEGIN, ...stack];
}

function end(stack) {
    return [constants.END, ...stack];
}

function cnst(op, stack) {
    return [op, ...stack];
}

function tru(value) {
    if (value === constants.BEGIN)
        throw new Error( 'Unexpected @{' );
    if (value === constants.END)
        throw new Error( 'Unexpected @}' );
    if (value === 0)
        return false;
    if (value === '')
        return false;
    if (value instanceof Array)
        return !!value[0];
    if (value === 'false')
        return false;
    
    return !!value;
}

function interval(seconds) {
    if (seconds === undefined)
        return undefined;

    const mins = Math.floor( seconds / 60 ),
        hours = Math.floor( mins / 60 ),
        days = Math.floor( hours / 24 );

    let result = '';
    if (days > 0)
        result += `${ifn( 'units::count days', days )} `;
    if (hours % 24 > 0)
        result += `${ifn( 'units::count hours', hours % 24 )} `;

    if (mins % 60 > 0 && days === 0)
        result += `${ifn( 'units::count minutes', mins % 60 )} `;
    else if (mins % 60 > 0)
        result = `~${result}`;
    else if (days === 0 && hours == 0)
        result = ifn( 'units::less than minute' );

    return result.trim();
}




// WEBPACK FOOTER //
// ./src/js/lib/handlebars.helpers.js